import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import NauticalDepthBackground from "@/components/NauticalDepthBackground";
import NauticalWaveOverlay from "@/components/NauticalWaveOverlay";

const MotionLink = motion(Link);

// ─── Data ────────────────────────────────────────────────────────────────────
// To add a new pillar: append an object to this array.
// To remove one: delete its object.
// imageFirst: true  → image on the LEFT on desktop (text on right)
// imageFirst: false → image on the RIGHT on desktop (text on left)
type Pillar = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  image: string;
  imageFirst: boolean;
  href?: string;
  ctaNextTab?: boolean;
  ctaLabel?: string;
};

const pillars: Pillar[] = [
  {
    id: "sobre-nos-pilar",
    title: "Sobre Nós",
    subtitle: "7 ANOS TRANSFORMANDO O MAR",
    description:
      "A JL Escola Náutica nasceu da paixão pelo mar e da necessidade de um padrão de excelência no setor. Com 7 anos de história, consolidamos nossa presença como a principal referência em formação náutica e assessoria documental técnica no país.",
    features: [
      "7 anos de atuação ininterrupta",
      "Liderança absoluta em aprovação na Marinha",
      "Compromisso inegociável com a segurança no mar",
      "Experiência premium focada no cliente",
    ],
    image: "/assets/Equipe.jpeg",
    imageFirst: true,
    ctaNextTab: true,
    ctaLabel: "Deslize e descubra o que nos diferencia",
  },
  {
    id: "dominio",
    title: "Domínio Técnico",
    subtitle: "ONDE CONHECIMENTO VIRA COMANDO",
    description:
      "Décadas de mar formaram uma equipe que não ensina teoria — transmite experiência. Nossos instrutores acumulam milhares de milhas navegadas e índice zero de reprovação em exames da Marinha.",
    features: [
      "Instrutores com +10 anos de navegação ativa",
      "Metodologia forjada na prática, não em apostilas",
      "Índice zero de reprovação nos exames oficiais",
      "Formação que a Capitania dos Portos reconhece de imediato",
    ],
    image: "/assets/m.jfif",
    imageFirst: false,
    href: "/escola",
  },
  {
    id: "presenca",
    title: "Presença Nacional",
    subtitle: "DO LITORAL AO PLANALTO DE SANTA CATARINA",
    description:
      "4 bases estratégicas onde o armador precisa estar. Não operamos por telefone — estamos no cais, no porto e na capitania. Presença física é o que separa assessoria de promessa.",
    features: [
      "4 unidades próprias em operação",
      "Balneário Camboriú, São Francisco do Sul, Barra do Sul e Canoinhas",
      "Proximidade com Capitanias e Delegacias Marítimas",
      "Atendimento presencial com hora marcada",
    ],
    image: "/assets/BC.jpeg",
    imageFirst: true,
    href: "/unidades",
  },
  {
    id: "precisao",
    title: "Precisão Documental",
    subtitle: "BUROCRACIA RESOLVIDA. PONTO.",
    description:
      "Enquanto outros tentam, nós tramitamos. Registro, transferência, legalização — cada documento passa por um crivo técnico rigoroso antes de chegar à Marinha. Resultado: aprovação no primeiro protocolo.",
    features: [
      "Processo agilizado com entrega direta na Marinha",
      "Revisão rigorosa de cada documento para evitar erros",
      "Acompanhamento em tempo real do seu processo",
      "Aprovação consistente no primeiro envio",
    ],
    image: "/assets/EN.png",
    imageFirst: false,
    href: "/despachante",
  },
];

// ─── Component ───────────────────────────────────────────────────────────────
const ServicePillarsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(0);

  const goTo = (index: number) => {
    if (index === activeIndex) return;
    setPrevIndex(activeIndex);
    setActiveIndex(index);
  };

  // Monitorar hash e eventos para ativar a aba "Sobre Nós"
  useEffect(() => {
    const handleActivate = () => {
      goTo(0);
    };

    const handleHashChange = () => {
      if (window.location.hash === "#diferenciais") {
        handleActivate();
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("jl-active-sobre-nos", handleActivate);

    // Verificar no carregamento inicial
    handleHashChange();

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("jl-active-sobre-nos", handleActivate);
    };
  }, []);

  // Direction: 1 = slide left-to-right (forward), -1 = slide right-to-left (backward)
  const direction = activeIndex >= prevIndex ? 1 : -1;
  const pillar = pillars[activeIndex];

  return (
    <section
      id="diferenciais"
      className="relative bg-primary py-16 sm:py-24 overflow-hidden"
    >
      <NauticalDepthBackground />
      <NauticalWaveOverlay />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">

        {/* ── Title ── */}
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-medium text-primary-foreground">
            O Padrão que Define o Setor.
          </h2>
        </div>

        {/* ── Tab Pills ── */}
        <div className="mb-12">
          {/* Mobile: grid 4 cols */}
          <div className="grid grid-cols-4 gap-0 sm:hidden rounded-xl overflow-hidden border border-gold/20 bg-white/5">
            {pillars.map((p, i) => (
              <button
                key={p.id}
                onClick={() => goTo(i)}
                className={`py-2.5 text-xs font-medium text-center transition-all duration-300 ${activeIndex === i
                  ? "bg-gold text-primary shadow-md"
                  : "text-primary-foreground/60"
                  }`}
              >
                {p.title}
              </button>
            ))}
          </div>
          {/* Desktop: inline pills */}
          <div className="hidden sm:flex justify-center">
            <div className="flex items-center gap-2 rounded-full p-1.5 bg-white/5 border border-gold/20">
              {pillars.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => goTo(i)}
                  className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap ${activeIndex === i
                    ? "bg-gold text-primary shadow-md"
                    : "text-primary-foreground/60 hover:text-primary-foreground hover:bg-white/5"
                    }`}
                >
                  {p.title}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Animated Content ── */}
        {/*
          overflow-hidden here prevents the sliding animation from creating
          a horizontal scrollbar or spilling outside the section.
        */}
        <div className="max-w-5xl mx-auto overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={pillar.id}
              initial={{ opacity: 0, x: direction * 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction * -50 }}
              transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
            >
              {/* Image — always on top on mobile; alternates on desktop */}
              <div
                className={
                  pillar.imageFirst ? "order-1 lg:order-1" : "order-1 lg:order-2"
                }
              >
                <div className="relative w-full aspect-square max-w-sm mx-auto lg:max-w-none overflow-hidden rounded-2xl border border-white/10 shadow-card">
                  <img
                    src={pillar.image}
                    alt={pillar.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  {/* Subtle vignette at the bottom */}
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/25 via-transparent to-transparent pointer-events-none" />
                </div>
              </div>

              {/* Text — always below image on mobile; alternates on desktop */}
              <div
                className={
                  pillar.imageFirst ? "order-2 lg:order-2" : "order-2 lg:order-1"
                }
              >
                <span className="text-xs uppercase tracking-[0.3em] text-gold mb-3 block">
                  {pillar.subtitle}
                </span>
                <h3 className="font-heading text-3xl sm:text-4xl font-medium text-primary-foreground mb-4">
                  {pillar.title}
                </h3>
                <p className="text-primary-foreground/75 leading-relaxed mb-6">
                  {pillar.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {pillar.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <div className="mt-1.5 w-1.5 h-1.5 shrink-0 rounded-full bg-gold" />
                      <span className="text-sm text-primary-foreground/80 leading-snug">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                {pillar.ctaNextTab ? (
                  <motion.button
                    onClick={() => goTo(activeIndex + 1)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-outline-gold px-6 py-3 rounded-lg text-sm font-medium inline-flex items-center gap-2 group"
                  >
                    {pillar.ctaLabel}
                    <svg
                      className="w-4 h-4 transition-transform group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 5l7 7-7 7M5 5l7 7-7 7"
                      />
                    </svg>
                  </motion.button>
                ) : (
                  <MotionLink
                    to={pillar.href!}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-outline-gold px-6 py-3 rounded-lg text-sm font-medium inline-flex items-center gap-2 group"
                  >
                    Saiba Mais
                    <svg
                      className="w-4 h-4 transition-transform group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </MotionLink>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ── Dot indicators (mobile-friendly) ── */}
        <div className="flex justify-center gap-2 mt-10">
          {pillars.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`transition-all duration-300 rounded-full ${activeIndex === i
                ? "w-6 h-2 bg-gold"
                : "w-2 h-2 bg-white/20 hover:bg-white/40"
                }`}
              aria-label={`Ir para ${pillars[i].title}`}
            />
          ))}
        </div>

      </div>
    </section>
  );
};

export default ServicePillarsSection;
